import type { ContactoItem, ContactosFields } from '@/shared/api/controllers/contactos';
import type { ContactFormValues } from './schema';
import type { MedioDeLlegada } from './constants';
import { CONDICION_CLIENTE_EXTERIOR, MEDIOS_DE_LLEGADA } from './constants';

function asMedio(v: string | null | undefined): MedioDeLlegada | undefined {
  return (MEDIOS_DE_LLEGADA as readonly string[]).includes(v ?? '')
    ? (v as MedioDeLlegada)
    : undefined;
}

/** SharePoint item → form values (for the edit dialog prefill). */
export function itemToForm(item: ContactoItem): ContactFormValues {
  const f = item.fields;
  return {
    nombre: f.Nombre_C ?? '',
    apellido: f.Apellido_C ?? '',
    mail: f.Mail_C ?? '',
    celular: f.Celular_C ?? '',
    medioDeLlegada: asMedio(f.MedioDeLlegada_C),
    referido: f.Referido_C ?? '',
    cuit: f.CUIT_C ?? '',
    razonSocial: f.RazonSocial_C ?? '',
    // condicionIVA holds Condición IVA on AR and Régimen Fiscal on MX
    // — both stored in the legacy CP_C column. Schema is z.string()
    // so we pass through any value; MX values used to be dropped by a
    // PROVINCIAS_ARGENTINA / CONDICIONES_IVA narrow that's now gone.
    condicionIVA: f.CP_C ?? undefined,
    provincia: f.Provincia_C ?? undefined,
    ciudad: f.Ciudad_C ?? '',
    direccion: f.Direccion_C ?? '',
    altura: f.Altura_C ?? '',
    piso: f.Piso_C ?? '',
    depto: f.Depto_C ?? '',
    codigoPostal: f.CodigoPostal_C ?? '',
    usoCfdi: f.UsoCFDI_C ?? undefined,
    direccionCompleta: f.DireccionCompleta_C ?? '',
    recibe: f.Receptor_C ?? '',
    observaciones: f.Observaciones_C ?? '',
    pctMinimoEcheq: f.PctMinimoEcheq_C != null ? String(f.PctMinimoEcheq_C).replace('.', ',') : '',
    comisionPropia: f.ComisionVendedor_C != null,
    comisionVendedor: f.ComisionVendedor_C != null ? String(f.ComisionVendedor_C).replace('.', ',') : '',
    paisDestinoArca: f.PaisDestinoArca_C || undefined,
    tipoEntidadExterior: f.TipoEntidadExterior_C || undefined,
    idImpositivoExterior: f.IdImpositivoExterior_C ?? '',
  };
}

/**
 * Form values → SharePoint fields payload (for create/update).
 * `conPctEcheq`: sólo admin/administración mandan el % de E-cheq (el server
 * se lo rechaza a ventas). `conComision`: ídem la comisión por cliente.
 */
export function formToFields(
  values: ContactFormValues,
  { conPctEcheq = false, conComision = false }: { conPctEcheq?: boolean; conComision?: boolean } = {},
): Partial<ContactosFields> {
  const trim = (v: string | undefined | null) => (v && v.trim() ? v.trim() : null);
  // Cliente del Exterior: no tiene CUIT argentino ni provincia, y los datos de exportación no
  // deben quedar colgados de un contacto que ya no es del exterior.
  const exterior = values.condicionIVA === CONDICION_CLIENTE_EXTERIOR;
  return {
    Nombre_C: values.nombre.trim(),
    Apellido_C: values.apellido.trim(),
    Mail_C: trim(values.mail) ?? '',
    Celular_C: values.celular.trim(),
    MedioDeLlegada_C: values.medioDeLlegada ?? '',
    Referido_C: values.medioDeLlegada === 'Referidos' ? (trim(values.referido) ?? '') : '',
    CUIT_C: exterior ? '' : (trim(values.cuit) ?? ''),
    RazonSocial_C: trim(values.razonSocial) ?? '',
    CP_C: values.condicionIVA ?? '',
    Provincia_C: exterior ? '' : (values.provincia ?? ''),
    Ciudad_C: trim(values.ciudad) ?? '',
    Direccion_C: trim(values.direccion) ?? '',
    Altura_C: trim(values.altura) ?? '',
    Piso_C: trim(values.piso) ?? '',
    Depto_C: trim(values.depto) ?? '',
    CodigoPostal_C: trim(values.codigoPostal) ?? '',
    UsoCFDI_C: values.usoCfdi ?? '',
    DireccionCompleta_C: trim(values.direccionCompleta) ?? '',
    Receptor_C: trim(values.recibe) ?? '',
    Observaciones_C: trim(values.observaciones) ?? '',
    PaisDestinoArca_C: exterior ? (values.paisDestinoArca ?? '') : '',
    TipoEntidadExterior_C: exterior ? (values.tipoEntidadExterior ?? '') : '',
    IdImpositivoExterior_C: exterior ? (trim(values.idImpositivoExterior) ?? '') : '',
    // '' → el server guarda null (usa el parámetro general).
    ...(conPctEcheq ? { PctMinimoEcheq_C: trim(values.pctMinimoEcheq) ?? '' } : {}),
    ...(conComision
      ? { ComisionVendedor_C: values.comisionPropia ? (trim(values.comisionVendedor) ?? '') : '' }
      : {}),
  };
}
